import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import { Reveal, Overline } from "../components/Reveal";
import { SUBCATEGORIES } from "../lib/catalog";

const TITLES = { men: "Men", women: "Women", kids: "Kids" };

export default function Shop({ gender, preloved, newArrivals, sale }) {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState(null);
  const [brands, setBrands] = useState([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sort, setSort] = useState("newest");
  const [sizeF, setSizeF] = useState("");
  const [colorF, setColorF] = useState("");
  const [inStock, setInStock] = useState(false);

  const q = params.get("q") || "";
  const category = params.get("category") || "";
  const brand = params.get("brand") || "";
  const condition = params.get("condition") || "";

  const apiParams = useMemo(() => {
    const p = {};
    if (gender) p.gender = gender;
    if (preloved) p.preloved = true;
    if (newArrivals) p.new_arrivals = true;
    if (sale) p.sale = true;
    if (q) p.q = q;
    if (category) p.category = category;
    if (brand) p.brand = brand;
    if (condition === "new") p.condition = "new";
    else if (condition) { p.preloved = true; p.condition = condition; }
    return p;
  }, [gender, preloved, newArrivals, sale, q, category, brand, condition]);

  useEffect(() => {
    setProducts(null);
    api.get("/products", { params: apiParams }).then((r) => setProducts(r.data)).catch(() => setProducts([]));
  }, [apiParams]);
  useEffect(() => { api.get("/brands").then((r) => setBrands(r.data)).catch(() => {}); }, []);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };

  const title = q ? `Results for “${q}”` : gender ? TITLES[gender] : preloved ? "Pre-Loved" : newArrivals ? "New Arrivals" : sale ? "Sale" : "Shop All";
  const subs = gender ? SUBCATEGORIES[gender] : [...new Set(Object.values(SUBCATEGORIES).flat())];

  const allSizes = useMemo(() => [...new Set((products || []).flatMap((p) => p.sizes || []))].sort(), [products]);
  const allColors = useMemo(() => [...new Set((products || []).map((p) => p.color).filter(Boolean))].sort(), [products]);
  const displayed = useMemo(() => {
    let list = [...(products || [])];
    if (sizeF) list = list.filter((p) => (p.sizes || []).includes(sizeF));
    if (colorF) list = list.filter((p) => p.color === colorF);
    if (inStock) list = list.filter((p) => p.stock > 0);
    if (sort === "price-asc") list.sort((a, b) => a.price - b.price);
    else if (sort === "price-desc") list.sort((a, b) => b.price - a.price);
    else if (sort === "discount") list.sort((a, b) => ((b.mrp - b.price) / b.mrp) - ((a.mrp - a.price) / a.mrp));
    return list;
  }, [products, sizeF, colorF, inStock, sort]);

  const Filters = () => (
    <div className="space-y-8" data-testid="filters-panel">
      <div>
        <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-3">Condition</p>
        {[{ k: "", l: "Everything" }, { k: "new", l: "New" }, { k: "like new", l: "Pre-Loved · Like New" }, { k: "excellent", l: "Pre-Loved · Excellent" }, { k: "very good", l: "Pre-Loved · Very Good" }, { k: "good", l: "Pre-Loved · Good" }].map((c) => (
          <button key={c.k} onClick={() => setParam("condition", c.k)} data-testid={`filter-condition-${c.k || "all"}`}
            className={`block w-full text-left text-sm py-1.5 transition-colors ${condition === c.k ? "text-wine font-normal" : "text-ink/60 hover:text-ink"}`}>
            {c.l}
          </button>
        ))}
      </div>
      <div>
        <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-3">Category</p>
        <button onClick={() => setParam("category", "")} data-testid="filter-category-all"
          className={`block w-full text-left text-sm py-1.5 ${!category ? "text-wine font-normal" : "text-ink/60 hover:text-ink"}`}>All Categories</button>
        {subs.map((c) => (
          <button key={c} onClick={() => setParam("category", c)} data-testid={`filter-category-${c.toLowerCase().replace(/[^a-z]/g, "-")}`}
            className={`block w-full text-left text-sm py-1.5 transition-colors ${category === c ? "text-wine font-normal" : "text-ink/60 hover:text-ink"}`}>
            {c}
          </button>
        ))}
      </div>
      <div>
        <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-3">Brand</p>
        <button onClick={() => setParam("brand", "")} data-testid="filter-brand-all"
          className={`block w-full text-left text-sm py-1.5 ${!brand ? "text-wine font-normal" : "text-ink/60 hover:text-ink"}`}>All Brands</button>
        {brands.map((b) => (
          <button key={b.slug} onClick={() => setParam("brand", b.slug)} data-testid={`filter-brand-${b.slug}`}
            className={`block w-full text-left text-sm py-1.5 transition-colors ${brand === b.slug ? "text-wine font-normal" : "text-ink/60 hover:text-ink"}`}>
            {b.name} <span className="text-muted-foreground text-xs">({b.product_count})</span>
          </button>
        ))}
      </div>
      {allSizes.length > 0 && (
        <div>
          <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-3">Size</p>
          <div className="flex flex-wrap gap-1.5">
            {allSizes.map((s) => (
              <button key={s} onClick={() => setSizeF(sizeF === s ? "" : s)} data-testid={`filter-size-${s}`}
                className={`min-w-10 px-2 py-2 border text-xs transition-colors ${sizeF === s ? "bg-ink text-ivory border-ink" : "border-border hover:border-ink"}`}>{s}</button>
            ))}
          </div>
        </div>
      )}
      {allColors.length > 0 && (
        <div>
          <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-3">Color</p>
          <div className="flex flex-wrap gap-1.5">
            {allColors.map((c) => (
              <button key={c} onClick={() => setColorF(colorF === c ? "" : c)} data-testid={`filter-color-${c.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className={`px-3 py-2 border text-xs transition-colors ${colorF === c ? "bg-ink text-ivory border-ink" : "border-border hover:border-ink"}`}>{c}</button>
            ))}
          </div>
        </div>
      )}
      <div>
        <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-3">Availability</p>
        <label className="flex items-center gap-2.5 text-sm text-ink/60 cursor-pointer">
          <input type="checkbox" checked={inStock} onChange={(e) => setInStock(e.target.checked)} data-testid="filter-in-stock" className="accent-[#4A0E17] w-4 h-4" />
          In stock only
        </label>
      </div>
    </div>
  );

  return (
    <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-14 md:py-20" data-testid="shop-page">
      <Reveal>
        <Overline>{gender ? "Department" : "Jigel"}</Overline>
        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter mt-3">{title}</h1>
        {preloved && <p className="mt-4 max-w-xl text-ink/60 text-sm leading-relaxed">Curated pre-loved inventory — independently sourced and professionally inspected. Trade-In garments never appear here.</p>}
      </Reveal>
      <div className="mt-6 flex items-center gap-4 lg:hidden">
        <button onClick={() => setFiltersOpen(true)} data-testid="open-filters-btn"
          className="flex items-center gap-2 border border-ink px-4 py-2.5 text-[11px] tracking-[0.2em] uppercase">
          <SlidersHorizontal size={14} strokeWidth={1.5} /> Filters
        </button>
      </div>
      <div className="mt-10 grid lg:grid-cols-[240px_1fr] gap-12">
        <aside className="hidden lg:block"><div className="sticky top-28"><Filters /></div></aside>
        <div>
          {products === null ? (
            <div className="py-32 text-center font-serif text-2xl text-muted-foreground">Curating…</div>
          ) : products.length === 0 ? (
            <div className="py-32 text-center" data-testid="no-results">
              <p className="font-serif text-3xl">Nothing found.</p>
              <p className="mt-3 text-sm text-muted-foreground">Try a different search or clear your filters.</p>
              <Link to="/shop" data-testid="clear-filters" className="inline-block mt-6 text-[11px] tracking-[0.25em] uppercase text-wine underline underline-offset-4">Clear all filters</Link>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-8">
                <p className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground" data-testid="result-count">{displayed.length} pieces</p>
                <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="sort-select"
                  className="bg-transparent border border-border px-3 py-2 text-[11px] tracking-[0.15em] uppercase outline-none focus:border-wine">
                  <option value="newest">Sort: Newest</option>
                  <option value="price-asc">Price: Low to High</option>
                  <option value="price-desc">Price: High to Low</option>
                  <option value="discount">Biggest Savings</option>
                </select>
              </div>
              <div className="grid grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-x-5 gap-y-12">
                {displayed.map((p, i) => <ProductCard key={p.product_id} product={p} index={i} />)}
              </div>
            </>
          )}
        </div>
      </div>
      {filtersOpen && (
        <div className="fixed inset-0 z-[60] bg-ivory overflow-y-auto p-6 lg:hidden" data-testid="mobile-filters">
          <div className="flex justify-between items-center mb-8">
            <p className="font-serif text-2xl">Filters</p>
            <button onClick={() => setFiltersOpen(false)} data-testid="close-filters-btn" aria-label="Close filters"><X size={20} strokeWidth={1.5} /></button>
          </div>
          <Filters />
          <button onClick={() => setFiltersOpen(false)} data-testid="apply-filters-btn"
            className="mt-10 w-full bg-ink text-ivory py-4 text-[11px] tracking-[0.25em] uppercase">Show Results</button>
        </div>
      )}
    </div>
  );
}
